import type { JSX } from "react";
import { YuLifeLocale } from "hooks";
import { StaticRoutes } from "@routes";
import { Img } from "./styles";
import { ReactElement } from "react";
import { VideoPlayer } from "components/atoms/VideoPlayer";
import { P1 } from "components/atoms/Paragraph";
import { YULIFE_DATA } from "utils/_constants";
import { Anchor } from "components/atoms/Anchor";
import { color } from "@styles";

type Data = Record<
  YuLifeLocale,
  {
    quotes: Quote[];
    products: Product[];
    yumojiImage?: YumojiImage;
    prodSection: ProdSection;
    tabs?: Tab[];
  }
>;

interface Quote {
  quote: string;
  avatar?: string;
  source: string;
  caseStudyURL?: string;
  vimeoId?: string;
}

interface Product {
  iconName?: string;
  title: string;
  description: string;
  href?: string;
  isLive?: boolean;
}

interface YumojiImage {
  imageDetails: ReactElement;
}

interface ProdSection {
  title: string;
  copy: string | ReactElement;
}

interface Tab {
  title: string;
  heading?: string | JSX.Element;
  content?: string | JSX.Element;
  image?: string | JSX.Element;
  alt?: string;
  url?: string;
  callToAction?: string;
  footnote?: string;
}

const data: Data = {
  uk: {
    quotes: [
      {
        quote:
          "YuLife ticked all of the boxes. It was the answer to our insurance needs. Wellness and engagement in benefits that employees were not using previously.",
        source: "Alix Hobbs, Director of Operations, Cloud International",
        avatar: "/static/img/carousel/alix-cloud.png",
        vimeoId: "769492152",
      },
      {
        quote:
          "Insurance with an app that focuses on health and wellbeing is an excellent way to take an active role in the wellbeing of your employees, and show them you care!",
        source: "Maddie Haycock, Office Administrator, xDesign",
      },
      {
        quote:
          "YuLife found a way to help us engage with insurance, whereas it’s usually considered a benefit ‘in the background’.",
        source: "Ciara Stacey, Head of People, Signal AI",
        caseStudyURL: "https://yulife.com/case-studies/signal-ai-increasing-employee-engagement/",
      },
    ],
    products: [
      {
        iconName: "Health",
        title: "Group Health Insurance",
        description:
          "Covers the cost of high-quality healthcare, quickly, if employees were to get ill. Provided by Bupa.",
        href: StaticRoutes.insuranceGroupHealthInsurance,
      },
      {
        title: "Group Life Insurance",
        iconName: "GroupLife",
        description: "Financial support for employees' beneficiaries in the event of their death.",
        href: StaticRoutes.insuranceGroupLifeInsurance,
      },
      {
        title: "Group Income Protection",
        iconName: "SickPayIcon",
        description:
          "Covers a proportion of an employee’s income in the event that they are unable to work due to ill health.",
        href: StaticRoutes.insuranceGroupIncomeProtection,
      },
      {
        title: "Group Dental Insurance",
        iconName: "GroupDental",
        description:
          "Easy-access financial cover for oral treatment - for your family, and their families. Provided by Bupa.",
        href: StaticRoutes.insuranceGroupDentalInsurance,
      },
    ],
    yumojiImage: {
      imageDetails: (
        <VideoPlayer
          webm="/static/pages/group-insurance/Always-on-Insurance-2.mp4"
          $maxWidth="31.25rem"
        />
      ),
    },
    prodSection: {
      title: "How it works",
      copy: "Group Insurance is an insurance policy that covers a group of people, such as employees – group life insurance to group dental cover – and forms a fundamental part of employee benefits programme supporting financial, physical and mental wellbeing.",
    },
  },
  us: {
    quotes: [
      {
        quote:
          "YuLife ticked all of the boxes. It was the answer to our insurance needs. Wellness and engagement in benefits that employees were not using previously.",
        source: "Alix Hobbs, Director of Operations, Cloud International",
        avatar: "/static/img/carousel/alix-cloud.png",
        vimeoId: "769492152",
      },
      {
        quote:
          "YuLife found a way to help us engage with insurance, whereas it’s usually considered a benefit ‘in the background’.",
        source: "Ciara Stacey, Head of People, Signal AI",
        caseStudyURL: "https://yulife.com/case-studies/signal-ai-increasing-employee-engagement/",
      },
    ],
    products: [
      {
        title: "Life Insurance",
        iconName: "",
        description:
          "Provide peace of mind with financial support for their loved ones through life’s most difficult moments.",
      },
      {
        title: "Disability Insurance",
        iconName: "",
        description:
          "Support employees with income replacement if they’re sick or injured and cannot work.",
      },
      {
        title: "Vision Insurance",
        iconName: "",
        description:
          "Promote good eye health with vision coverage for exams, glasses and contact lenses.",
      },
      {
        title: "Dental Insurance",
        iconName: "",
        description:
          "Promote good oral health with dental coverage from routine cleaning to root canals.",
      },
      {
        title: "Accident Insurance",
        iconName: "",
        description:
          "Minimize the out-of-pockets costs that result from an unexpected accidental injury.",
      },
      {
        title: "Critical Illness Insurance",
        iconName: "",
        description:
          "Offset the expenses associated with the treatment and recovery of a specified critical illness.",
      },
      {
        title: "Hospital Insurance",
        iconName: "",
        description:
          "Reduce and ease the unplanned financial burdens of an unexpected hospital admission.",
      },
      {
        title: "Cancer Insurance",
        iconName: "",
        description:
          "Provide peace of mind with eased financial impact associated with the treatment and recovery of cancer.",
      },
    ],
    yumojiImage: {
      imageDetails: null,
    },
    prodSection: {
      title: "How it works",
      copy: (
        <>
          <P1 align="center">
            Group Insurance is an insurance policy that covers a group of people, such as employees,
            and is a fundamental part of an employee benefits program that supports financial,
            physical and mental wellbeing.
          </P1>
          <P1 align="center">
            At YuLife, we transform the way people engage with insurance by partnering with leading
            insurance carriers to bring together protection, rewards and benefits, all in one
            award-winning app. The end result is healthier individuals, thriving organizations and
            lower insurance risk.
          </P1>
        </>
      ),
    },
    tabs: [
      {
        title: "Member app",
        heading: "An app that people love (and actually use)",
        content:
          "Each of your team members gets access to YuLife’s award-winning app. and are rewarded for doing everyday, healthy activities from walking to cycling to meditating. As far as engagement goes - an average of 2 in 3 employees download the app, and 1 in 2 interact with it weekly!¹",
        image: (
          <img
            src="/static/pages/businesses/yulife-us-yucoin-rewards-app.png"
            alt="The YuLife app showing the “Today’s Earnings” page  followed by the users YuCoin count that’s sitting at “280 YuCoin”, and their daily core wellbeing activities that earned them redeemable for rewards like steps and miles cycled."
          />
        ),
        url: "/us/features/employee-app",
        footnote: `¹ ${YULIFE_DATA}`,
      },
      {
        title: "Rewards",
        heading: "Reward your people & protect the planet",
        content: (
          <P1 pt="0">
            Employees earn{" "}
            <Anchor
              href="/us/blog/what-is-yucoin-and-how-does-it-work/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: `${color.yulifePink.P100}` }}
            >
              YuCoin
            </Anchor>{" "}
            for completing wellbeing activities to unlock real-world rewards – from retail gift
            cards, to charitable donations. Since using the YuLife app, nearly 90% of YuLifers
            report feeling happier and more productive!²
          </P1>
        ),
        image: (
          <VideoPlayer
            webm="/static/pages/insurance-carriers/yulife-us-rewards-screen.mp4"
            center
          />
        ),
        alt: "A GIF of the rewards section of the YuLife US app showing the Starbucks, Earthly and ShareTheMeal.",
        url: "/us/features/rewards",
        footnote: "² YuLife user survey, 2022",
      },
      {
        title: "Wellbeing Hub",
        heading: "A “one-stop shop” for employee benefits",
        content:
          "Lower your administrative burden by having one place to house all your benefits. YuLife’s Wellbeing Hub displays your key intervention tools like EAP and other employee benefits in one easy-to-access place. It is completely customizable, and because of the simplicity of the YuLife app, employers have reported seeing a near 5x increase in utilization of their employee benefits!³",
        image: <VideoPlayer webm="/static/pages/Businesses/yulife-us-wellbeing-hub.mp4" center />,
        url: "/us/features/wellbeing-hub",
        footnote: `³ ${YULIFE_DATA}`,
      },
      {
        title: "Insights",
        heading: "Data that delivers",
        content:
          "We understand your need to capture and track employee wellbeing data. Our HR portal provides detailed insight into wellbeing patterns that point to behavioral trends, and reports that reveal real business ROI.",
        image: (
          <img
            src="/static/pages/insurance-carriers/yulife-us-employer-hr-portal-data-dashboard.png"
            alt="The YuLife employer portal dashboard, showing data available to managers. You can see the steps taken by employees, challenges and meditation minutes, and YuCoin earned."
          />
        ),
        url: "/us/features/hr-insights",
      },
    ],
  },
  za: {
    quotes: [
      {
        quote:
          "YuLife ticked all of the boxes. It was the answer to our insurance needs. Wellness and engagement in benefits that employees were not using previously.",
        source: "Alix Hobbs, Director of Operations, Cloud International",
        avatar: "/static/img/carousel/alix-cloud.png",
        vimeoId: "769492152",
      },
      {
        quote:
          "Insurance with an app that focuses on health and wellbeing is an excellent way to take an active role in the wellbeing of your employees, and show them you care!",
        source: "Maddie Haycock, Office Administrator, xDesign",
      },
      {
        quote:
          "YuLife found a way to help us engage with insurance, whereas it’s usually considered a benefit ‘in the background’.",
        source: "Ciara Stacey, Head of People, Signal AI",
        caseStudyURL: "https://yulife.com/case-studies/signal-ai-increasing-employee-engagement/",
      },
    ],
    products: [
      {
        title: "Group Life Insurance",
        description:
          "Pays out a multiple of salary or fixed amount to your employees’ beneficiaries on death.",
        href: StaticRoutes.insuranceGroupLifeInsurance,
      },
      {
        title: "Group Income Protection",
        description:
          "Covers a proportion of an employee’s income in the event that they are unable to work due to ill health.",
        href: StaticRoutes.insuranceGroupIncomeProtection,
      },
      {
        title: "Funeral Cover",
        description: "This will pay out a fixed amount to your employees' beneficiaries on death.",
        href: StaticRoutes.insuranceFuneralCover,
      },
      {
        title: "Lump Sum Disability",
        description:
          "Pays out a lump sum to the employee in the event of their permanent disability.",
        href: StaticRoutes.insuranceLumpSumDisability,
      },
      {
        title: "Critical Illness",
        description:
          "Pays a lump-sum to an employee who experiences one of the defined list of conditions. Two cover options are available, Core and Enhanced.",
        href: StaticRoutes.insuranceGroupCriticalIllness,
      },
      {
        title: "Education Benefit",
        href: StaticRoutes.insuranceEducationBenefit,
        description:
          "Provides money in the event of death or permanent disability (if selected) to help cover the costs of education and care needs.",
      },
    ],
    yumojiImage: {
      imageDetails: (
        <VideoPlayer
          webm="/static/pages/group-insurance/yulife-yumoji-yucoin-power.webm"
          width="544"
          height="544"
        />
      ),
    },
    prodSection: {
      title: "What is Group Insurance?",
      copy: "Group Insurance is an insurance policy that covers a group of people, such as employees – group life insurance to group funeral cover – and forms a fundamental part of an employee benefits programme supporting financial, physical and mental wellbeing.",
    },
  },
  jp: {
    quotes: [],
    products: [],
    prodSection: {
      title: "",
      copy: "",
    },
  },
};

export default data;
